import { ArpIssuedItemModelDto, ArpParseResultsModelDto } from '@treasury/api/channel';
import { OmegaDate } from '@treasury/omega/components/datepicker';
import { FiDate } from '../../../dates';
import { AccountDto, AuditItemDto, IssuedItemEntryType, IssuedItemType } from '../../types/arp';
import { IIssuedItem, IssuedItemPendingStatus, createIssuedItem } from './issued-items.mappings';

export interface SourceItem {
    userUniqueId: string;
    name: string;
}

export interface IssuedItemFileSearchParams {
    fileUniqueId?: string;
    fileName?: string;
    sourceList?: SourceItem[];
    totalSourceCount?: number;
    uploadedDate?: OmegaDate;
    processedDate?: OmegaDate;
    amount?: [string, number, number?];
}

/**
 * Maps ArpParseResultsModelDto from the processFile API to IIssuedItem array
 * for use in the issued items creation workflow.
 */
export function mapArpParseResultsToIssuedItems(
    parseResults: ArpParseResultsModelDto
): IIssuedItem[] {
    if (!parseResults?.issuedItems) {
        return [];
    }

    return parseResults.issuedItems.map((item: ArpIssuedItemModelDto) => {
        // Create a new issued item using the factory function
        const issuedItem = createIssuedItem();

        // Map the properties from ArpIssuedItemModelDto to IIssuedItem
        issuedItem.itemEntryType = (item.entryType as IssuedItemEntryType) || 'Transmitted';
        issuedItem.createdDate = item.createdDate ? new Date(item.createdDate) : new Date();
        issuedItem.dateIssued = item.dateIssued ? new Date(item.dateIssued) : undefined;
        issuedItem.voidedDate = item.voidDate ? new Date(item.voidDate) : undefined;
        issuedItem.postDate = item.postDate ? new Date(item.postDate) : undefined;
        // Map audit items with proper type conversion
        issuedItem.auditItems = (item.auditItems || []).map(auditItem => ({
            ...auditItem,
            createdByUserName: auditItem.createdByUserName || '',
        })) as AuditItemDto[];
        issuedItem.checkAmount = item.checkAmount || 0;
        issuedItem.checkNumber = item.checkNumber || undefined;
        issuedItem.payee = item.payee || '';
        // Map itemStatus to valid enum value or empty string
        issuedItem.itemStatus =
            item.itemStatus === 'Issued' ||
            item.itemStatus === 'Cleared' ||
            item.itemStatus === 'Returned' ||
            item.itemStatus === 'Void' ||
            item.itemStatus === 'Deleted' ||
            item.itemStatus === 'StopPay' ||
            item.itemStatus === 'StaleDate' ||
            item.itemStatus === 'Dormant' ||
            item.itemStatus === 'Failed' ||
            item.itemStatus === 'Submitted'
                ? item.itemStatus
                : '';
        issuedItem.type = (item.type as IssuedItemType) || 'Check';
        // Convert CompanyAccountModelDto to AccountDto structure
        issuedItem.account = item.account
            ? ({
                  ...item.account,
                  availableBalanceFormatted: '',
                  collectedBalanceFormatted: '',
                  currentBalanceFormatted: '',
              } as unknown as AccountDto)
            : undefined;
        issuedItem.pendingStatus = IssuedItemPendingStatus.None;

        return issuedItem;
    });
}

export class IssuedItemFileMappings {
    public static mapSearchParams(
        searchParams: IssuedItemFileSearchParams
    ): Array<Record<string, unknown>> {
        const {
            uploadedDate,
            fileName,
            sourceList,
            totalSourceCount,
            processedDate,
            fileUniqueId,
            amount,
        } = searchParams;
        const searchBys: Array<Record<string, unknown>> = [];

        if (uploadedDate && 'dates' in uploadedDate && uploadedDate.dates.length > 0) {
            const [fromDate, toDate = fromDate] = uploadedDate.dates;

            searchBys.push({
                name: 'UploadedDate',
                value: IssuedItemFileMappings.formatDateForApi(fromDate),
                secondValue: IssuedItemFileMappings.formatDateForApi(toDate),
                searchType: 'Between',
            });
        }

        if (fileName) {
            searchBys.push({
                name: 'FileName',
                value: fileName,
                searchType: 'Contains',
            });
        }

        if (
            Array.isArray(sourceList) &&
            sourceList.length > 0 &&
            sourceList.length < (totalSourceCount ?? Infinity)
        ) {
            const isSingle = sourceList.length === 1;
            searchBys.push({
                name: 'UploadedByUserUniqueId',
                ...(isSingle
                    ? { value: sourceList[0].userUniqueId }
                    : { values: sourceList.map(source => source.userUniqueId) }),
                searchType: 'Contains',
            });
        }

        if (processedDate && 'dates' in processedDate && processedDate.dates.length > 0) {
            const [fromDate, toDate = fromDate] = processedDate.dates;

            searchBys.push({
                name: 'ProcessedDate',
                value: IssuedItemFileMappings.formatDateForApi(fromDate),
                secondValue: IssuedItemFileMappings.formatDateForApi(toDate),
                searchType: 'Between',
            });
        }

        if (fileUniqueId) {
            searchBys.push({
                name: 'FileUniqueId',
                value: fileUniqueId,
                searchType: 'Equals',
            });
        }

        if (
            amount &&
            amount[0] === 'specific' &&
            amount[1] !== undefined &&
            Number(amount[1]) !== 0
        ) {
            searchBys.push({
                name: 'TotalItemAmount',
                value: amount[1],
                searchType: 'Equals',
            });
        } else if (
            amount &&
            amount[0] === 'range' &&
            amount[1] !== undefined &&
            amount[2] !== undefined &&
            !(Number(amount[1]) === 0 && Number(amount[2]) === 0)
        ) {
            searchBys.push({
                name: 'TotalItemAmount',
                value: amount[1],
                secondValue: amount[2],
                searchType: 'Between',
            });
        }

        return searchBys;
    }

    private static formatDateForApi(date: Date): string {
        return new FiDate(date).toIsoDate();
    }
}
